import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Building2, Landmark } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";

const locText = (l) => [l?.city, l?.state, l?.country].filter(Boolean).join(", ");

export function AffiliationSearch({ onChange, onCreated }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [exact, setExact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [role, setRole] = useState("");
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = q.trim();
    if (term.length < 2) { setResults([]); setExact(null); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/profile/affiliation-search", { params: { q: term } });
        setResults(data.results || []);
        setExact(data.exact || null);
      } catch { setResults([]); setExact(null); }
      finally { setLoading(false); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const pick = (r) => { setSelected(r); setOpen(false); setQ(""); setResults([]); setExact(null); };

  const quickCreate = async (kind) => {
    const name = q.trim();
    if (!name || creating) return;
    setCreating(kind);
    try {
      const { data } = await api.post("/profile/me/affiliations/quick-create", {
        kind, display_name: name, role: role.trim(),
      });
      onChange(data.affiliations_resolved);
      onCreated?.(data.org);
      toast.success(data.org?.handle
        ? `${data.org.display_name} created — @${data.org.handle}`
        : `${data.org?.display_name || name} created`);
      setOpen(false); setQ(""); setResults([]); setExact(null); setRole("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create that profile.");
    } finally { setCreating(""); }
  };

  const add = async () => {
    if (!selected) return;
    setAdding(true);
    try {
      const { data } = await api.post("/profile/me/affiliations", {
        target_type: selected.target_type, target_id: selected.id, role: role.trim(),
      });
      onChange(data.affiliations_resolved);
      setSelected(null); setRole("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add affiliation.");
    } finally { setAdding(false); }
  };

  return (
    <div className="flex flex-col gap-2 sm:flex-row">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="justify-start sm:flex-1"
            data-testid="affiliation-search-trigger"
          >
            {selected ? (
              <span className="flex items-center gap-2 truncate">
                {selected.target_type === "capital" ? <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" /> : <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />}
                <span className="truncate">{selected.display_name}</span>
              </span>
            ) : (
              <span className="flex items-center gap-2 text-muted-foreground"><Search className="h-4 w-4" /> Search businesses & capital profiles</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" data-testid="affiliation-search-popover">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Type a company or fund name…" value={q} onValueChange={setQ} data-testid="affiliation-search-input" />
            <CommandList>
              {!loading && q.trim().length >= 2 && results.length === 0 && (
                <CommandEmpty>No published profiles found.</CommandEmpty>
              )}
              {q.trim().length < 2 && <div className="px-3 py-2 text-xs text-muted-foreground">Type at least 2 characters to search.</div>}
              {loading && <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>}
              {results.map((r) => (
                <CommandItem
                  key={`${r.target_type}:${r.id}`}
                  value={`${r.target_type}:${r.id}`}
                  onSelect={() => pick(r)}
                  className="flex items-center gap-2"
                  data-testid={`affiliation-result-${r.id}`}
                >
                  <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded bg-secondary">
                    {r.profile_image_url ? <img src={fileUrl(r.profile_image_url)} alt="" className="h-full w-full object-cover" /> : (r.target_type === "capital" ? <Landmark className="h-3.5 w-3.5 text-muted-foreground" /> : <Building2 className="h-3.5 w-3.5 text-muted-foreground" />)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-[560]">{r.display_name}</span>
                      <Badge variant="outline" className="h-4 px-1 text-[10px] uppercase">{r.target_type}</Badge>
                    </div>
                    {(locText(r.location) || r.headline) && (
                      <div className="truncate text-xs text-muted-foreground">{locText(r.location) || r.headline}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
              {!loading && q.trim().length >= 2 && exact && (exact.business === false || exact.capital === false) && (
                <div className="border-t border-border p-1" data-testid="affiliation-quick-create">
                  <p className="px-2 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">Can&apos;t find it?</p>
                  {exact.business === false && (
                    <button
                      type="button"
                      disabled={!!creating}
                      onClick={() => quickCreate("business")}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-secondary disabled:opacity-60"
                      data-testid="affiliation-create-business"
                    >
                      <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{creating === "business" ? "Creating…" : <>Add business profile &ldquo;{q.trim()}&rdquo;</>}</span>
                    </button>
                  )}
                  {exact.capital === false && (
                    <button
                      type="button"
                      disabled={!!creating}
                      onClick={() => quickCreate("capital")}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-secondary disabled:opacity-60"
                      data-testid="affiliation-create-capital"
                    >
                      <Landmark className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{creating === "capital" ? "Creating…" : <>Add capital profile &ldquo;{q.trim()}&rdquo;</>}</span>
                    </button>
                  )}
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <Input placeholder="Your role" value={role} onChange={(e) => setRole(e.target.value)} className="sm:w-40" data-testid="affiliation-role" />
      <Button type="button" onClick={add} disabled={!selected || adding} data-testid="affiliation-add"><Plus className="mr-1 h-4 w-4" /> Add</Button>
    </div>
  );
}
